import React, { FC, useState } from 'react';
import { TaskState } from 'shovel-lib/types';
import colors from '../../utils/colors';
import t from '../../i18n/t';
import { MiniText } from '../../utils/typography';
import { Row } from '../common/layoutUtils';
import MaterialIcon from '../common/icons/MaterialIcon';
import TippyTooltip from '../common/tooltip/TippyTooltip';
import { CushionIcon } from './TaskCard.styles';
import { getCushionItemColor } from '../../utils/taskUtils';
import { minutesToDurationInHours } from '../../utils/filters';
import storage from '../../utils/storage';
import CompletedOnTag from './partials/CompletedOnTag';
import { hasValue } from '../../utils/validators';
import { Moment } from 'moment';
import { now } from 'shovel-lib/utils/timeUtils';
import { EmojiPreview } from '@components/common/emoji/EmojiPreview';
import styled from 'styled-components';

type Props = {
  state: TaskState;
  timeNeed: number;
  completedOn?: string;
  cushion?: number;
  isPile?: boolean;
  due?: Moment;
  isGraph?: boolean;
  theme?: any;
  noTooltip?: boolean;
  /** The sidebar's: works the Cushion out for its list, as the button above it does. */
  onCalculate?: () => void;
};

// Worked out as the tooltip opens (its content is drawn only while it shows), not when the card was last drawn: a card
// is drawn again only when its task changes, which can be hours later.
const OverdueFor = ({ due }: { due?: Moment }) => <>{`${now().from(due, true)} ${t.OVERDUE.toLowerCase()}`}</>;

const TaskStatus: FC<Props> = ({
  state,
  completedOn,
  timeNeed,
  cushion,
  isPile,
  isGraph,
  theme,
  due,
  noTooltip,
  onCalculate
}) => {
  const [cushionPercentage] = useState(storage.getCushionWarningPercentage);

  if (state === TaskState.COMPLETED) {
    if (completedOn) {
      return <CompletedOnTag completedOn={completedOn} />;
    } else
      return (
        <Row>
          <MaterialIcon name={'check'} color={colors.positive} size={13} />
          <MiniText color={colors.positive}>{t.COMPLETED}</MiniText>
        </Row>
      );
  }

  let target;
  let content;
  let width;

  if (hasValue(cushion)) {
    const color = getCushionItemColor(cushion!, timeNeed, cushionPercentage / 100);
    content = t.CUSHION;
    target = (
      <Row>
        <CushionIcon
          name={'insights'}
          size={13}
          color={color}
          backgroundColor={theme ? theme.courseColorFunction(color) : undefined}
        />
        <MiniText color={color} style={{ marginLeft: 2 }}>
          {minutesToDurationInHours(cushion!)}
        </MiniText>
      </Row>
    );
  }

  // Only in the sidebar, whose list the Cushion is worked out for; the icon itself works it out (Petr, 2026-10-15: its
  // tooltip sent students to "the purple button above", which isn't purple in every colour scheme, nor above a card
  // in a calendar popup, so those popups show no icon until there is a cushion).
  if (state === TaskState.ACTIVE && isPile && !!due && !hasValue(cushion)) {
    content = t.CALCULATE_CUSHION_MESSAGE;
    width = '150px';
    target = (
      <span
        onClick={
          onCalculate &&
          (e => {
            e.stopPropagation();
            onCalculate();
          })
        }
      >
        <CalculateIcon name={'insights'} size={13} color={colors.gray} clickable={!!onCalculate} />
      </span>
    );
  }

  if (state === TaskState.DO_LATER) {
    target = (
      <span>
        <EmojiPreview emoji={'🔥'} size={10} />
      </span>
    );
    content = <OverdueFor due={due} />;
  }

  if (!target) return null;
  if (noTooltip) return target;

  return <TippyTooltip target={target} content={content} width={width} />;
};

// A soft grey tint of its own on hover, not a fade.
const CalculateIcon = styled(CushionIcon)`
  &:hover {
    opacity: 1;
    background-color: ${({ theme, clickable }) =>
      clickable ? `color-mix(in srgb, ${colors.gray} 22%, ${theme.background})` : undefined};
  }
`;

export default TaskStatus;
